import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  CloseIcon,
  MenuIcon,
  ChevronDownIcon,
  ArrowRightIcon,
} from "@talladega/icons";
import LocaleButton from "@common/components/LocaleButton";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import { User } from "@modules/user/types/user";
import { LoginModalMode, loginMode } from "@modules/user/types/LoginModalMode";
import { createPortal } from "react-dom";
import useLogoutUserMutation from "@modules/user/hooks/useLogoutUserMutation";
import CustomerAvatar from "@components/CustomerAvatar";
import useClientSide from "@common/hooks/useClientSide";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import { useRouter } from "next/router";
import { Button } from "@talladega/components";
import LocalImage from "@common/components/LocalImage";
import {
  roadwiseDarkLogo,
  roadwiseFinancingDarkLogo,
  roadwiseFinancingWhiteLogo,
  roadwiseWarrantyDarkLogo,
  roadwiseWarrantyWhiteLogo,
  roadwiseWhiteLogo,
} from "@public/images";
import RoadwiseResumeStartPurchaseModal from "@modules/purchase/warranty/components/RoadwiseResumeStartPurchaseModal";
import DealCustomerTypeEnum from "@common/enums/DealCustomerTypeEnum";
import { getProvinceById } from "@talladega/types";
import { sources } from "@common/enums/SourceEnum";
import {
  aftermarketQuoteStep,
  showResumeQuoteStep,
} from "@modules/purchase/PurchaseStep";
import { tryGetActiveConsumerFinancingPurchase } from "@modules/purchase/consumer/types/ConsumerFinancing";
import useConsumerFinancing from "@modules/purchase/consumer/hooks/useConsumerFinancing";
import AvatarMenu from "./AvatarMenu";

type MobileNavBarProps = {
  user: User | undefined;
  isMenuOpen: boolean;
  onMenuOpenChange?: (isOpen: boolean) => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
  mode?: "dark" | "light";
};

export function deriveRoadwiseType(asPath: string, locale?: string) {
  const path = (asPath || "").split("?")[0].split("#")[0];

  const cleanedPath = path.replace(/^\/+/, "").split("/");

  const first = cleanedPath[0] === locale ? cleanedPath[1] : cleanedPath[0];

  if (first === "warranty") return "warranty" as const;
  if (first === "financing") return "financing" as const;
  return "generic" as const;
}

export function getBrandLogo({
  mode,
  type,
}: {
  mode: "dark" | "light";
  type: "warranty" | "financing" | "generic";
}) {
  const wantWhite = mode === "dark";
  if (type === "warranty") {
    return wantWhite ? roadwiseWarrantyWhiteLogo : roadwiseWarrantyDarkLogo;
  }
  if (type === "financing") {
    return wantWhite ? roadwiseFinancingWhiteLogo : roadwiseFinancingDarkLogo;
  }

  return wantWhite ? roadwiseWhiteLogo : roadwiseDarkLogo;
}

const MobileNavBar = ({
  user,
  isMenuOpen,
  onMenuOpenChange,
  onLoginClick,
  onLogoutClick,
  mode,
}: MobileNavBarProps) => {
  const isClient = useClientSide();
  const { t } = useTranslation(["common", "purchase"]);

  const closeMenu = () => onMenuOpenChange?.(false);

  return isClient
    ? createPortal(
        <div
          className={classNames(
            "fixed inset-x-0 top-[88px] z-50 mt-2 h-[calc(100vh-96px)] lg:hidden overflow-y-auto",
            {
              hidden: !isMenuOpen,
              "bg-secondary-900": mode === "dark",
              "bg-white": mode === "light",
            }
          )}
        >
          <div className="mx-4 h-full border-t border-white/10">
            <div className="flex h-full flex-col justify-between gap-8">
              <div className="mt-8 flex flex-col gap-4">
                {/* Home */}
                <Link
                  href="/"
                  onClick={closeMenu}
                  className={classNames(
                    "text-base hover:text-opacity-80",
                    mode === "dark" ? "text-white" : "text-gray-900"
                  )}
                >
                  {t("common:home")}
                </Link>

                {/* Financing Section */}
                <div>
                  <div
                    className={classNames(
                      "text-base font-semibold",
                      mode === "dark" ? "text-white" : "text-gray-900"
                    )}
                  >
                    {t("common:financing")}
                  </div>
                  <div className="mt-2 flex flex-col">
                    <Link
                      href="/financing"
                      onClick={closeMenu}
                      className={classNames(
                        "pl-6 py-2 rounded hover:opacity-80",
                        mode === "dark" ? "text-white" : "text-gray-900"
                      )}
                    >
                      {t("purchase:get_pre_approved")}
                    </Link>
                    <Link
                      href="/financing/how-it-works"
                      onClick={closeMenu}
                      className={classNames(
                        "pl-6 py-2 rounded hover:opacity-80",
                        mode === "dark" ? "text-white" : "text-gray-900"
                      )}
                    >
                      {t("purchase:how_it_works")}
                    </Link>
                    <Link
                      href="/financing/faq"
                      onClick={closeMenu}
                      className={classNames(
                        "pl-6 py-2 rounded hover:opacity-80",
                        mode === "dark" ? "text-white" : "text-gray-900"
                      )}
                    >
                      {t("common:faq")}
                    </Link>
                  </div>
                </div>

                {/* Warranty Section */}
                <div>
                  <div
                    className={classNames(
                      "text-base font-semibold",
                      mode === "dark" ? "text-white" : "text-gray-900"
                    )}
                  >
                    {t("common:warranty")}
                  </div>
                  <div className="mt-2 flex flex-col">
                    <Link
                      href="/warranty"
                      onClick={closeMenu}
                      className={classNames(
                        "pl-6 py-2 rounded hover:opacity-80",
                        mode === "dark" ? "text-white" : "text-gray-900"
                      )}
                    >
                      {t("purchase:get_a_free_quote")}
                    </Link>
                    <Link
                      href="/warranty/how-it-works"
                      onClick={closeMenu}
                      className={classNames(
                        "pl-6 py-2 rounded hover:opacity-80",
                        mode === "dark" ? "text-white" : "text-gray-900"
                      )}
                    >
                      {t("purchase:how_it_works")}
                    </Link>
                    <Link
                      href="/warranty/faq"
                      onClick={closeMenu}
                      className={classNames(
                        "pl-6 py-2 rounded hover:opacity-80",
                        mode === "dark" ? "text-white" : "text-gray-900"
                      )}
                    >
                      {t("common:faq")}
                    </Link>
                  </div>
                </div>

                {/* Contact */}
                <Link
                  href="/contact"
                  onClick={closeMenu}
                  className={classNames(
                    "text-base hover:text-opacity-80",
                    mode === "dark" ? "text-white" : "text-gray-900"
                  )}
                >
                  {t("common:contact_us")}
                </Link>

                {user?.isLoggedIn && (
                  <Link
                    href="/profile"
                    onClick={closeMenu}
                    className={classNames(
                      "text-base hover:text-opacity-80",
                      mode === "dark" ? "text-white" : "text-gray-900"
                    )}
                  >
                    {t("common:my_profile")}
                  </Link>
                )}
                {user?.isLoggedIn && (
                  <Link
                    href="/protection"
                    onClick={closeMenu}
                    className={classNames(
                      "text-base hover:text-opacity-80",
                      mode === "dark" ? "text-white" : "text-gray-900"
                    )}
                  >
                    {t("common:warranty_protection")}
                  </Link>
                )}
              </div>

              <div className="flex flex-col items-center gap-2 pb-4">
                <Link
                  href="/"
                  onClick={closeMenu}
                  className="w-full rounded-full border-none bg-secondary-50 px-4 py-2 text-center text-secondary-700 hover:bg-secondary-50"
                >
                  {t("purchase:get_a_free_quote")}
                </Link>
                {user && user.isLoggedIn ? (
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onLogoutClick();
                      closeMenu();
                    }}
                    className={classNames("w-full text-center", {
                      "text-white": mode === "dark",
                      "text-primary-700": mode === "light",
                    })}
                  >
                    {t("common:logout")}
                  </Link>
                ) : (
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onLoginClick();
                      closeMenu();
                    }}
                    className={classNames("w-full text-center", {
                      "text-white": mode === "dark",
                      "text-primary-700": mode === "light",
                    })}
                  >
                    {t("common:login")}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;
};

type NavDropdownItem = { label: string; href: string };
const NavDropdown = ({
  label,
  items,
  mode,
}: {
  label: string;
  items: NavDropdownItem[];
  mode: "dark" | "light";
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={classNames("flex items-center gap-2 leading-4", {
          "text-white hover:text-white/80": mode === "dark",
          "text-gray-900 hover:text-gray-900/80": mode === "light",
        })}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{label}</span>
        <ChevronDownIcon
          className={classNames("h-4 w-4 transition-transform", {
            "rotate-180": open,
          })}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 mt-3 w-72 rounded-xl border border-secondary bg-white shadow-lg z-50"
          role="menu"
        >
          <ul className="py-3">
            {items.map((it) => (
              <li key={it.href}>
                <Link
                  href={it.href}
                  onClick={() => setOpen(false)}
                  role="menuitem"
                  className="block px-5 py-3 rounded-lg hover:bg-gray-100"
                >
                  {it.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

type Props = {
  setShowLoginModal: Dispatch<SetStateAction<boolean>>;
  setLoginModalMode: Dispatch<SetStateAction<LoginModalMode>>;
  user: User | undefined;
  mode?: "dark" | "light";
};

const RoadwiseNavBar = ({
  setShowLoginModal,
  setLoginModalMode,
  user,
  mode = "dark",
}: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const logoutMutation = useLogoutUserMutation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setIsAvatarMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onLoginClick = () => {
    setLoginModalMode(loginMode);
    setShowLoginModal(true);
  };
  const onAvatarClick = () => {
    setIsAvatarMenuOpen((s) => !s);
  };
  const onLogoutClick = () => {
    setIsAvatarMenuOpen(false);
    if (user?.isLoggedIn) {
      logoutMutation.mutate();
    }
  };

  const router = useRouter();
  const locale = router.locale || "en";

  const modalType = deriveRoadwiseType(router.asPath, locale);

  const { purchase: purchaseResponse } = useAftermarketPurchase();
  const aftermarketPurchase = tryGetActiveAftermarketPurchase(purchaseResponse);

  const { consumerFinancing: consumerFinancingOrNotFound } =
    useConsumerFinancing();

  const consumerFinancingPurchase = tryGetActiveConsumerFinancingPurchase(
    consumerFinancingOrNotFound
  );

  const [isGetStartedModalOpen, setIsGetStartedModalOpen] = useState(false);
  const handleGetStartedModal = () => setIsGetStartedModalOpen(true);

  const brandLogo = getBrandLogo({ mode, type: modalType });
  const logoSrc = locale === "en" ? brandLogo.srcEn : brandLogo.srcFr;

  const mainApplicant = aftermarketPurchase?.customers.find(
    (c) => c.typeId === DealCustomerTypeEnum.MainApplicant
  );

  const provinceAbbr = mainApplicant?.currentAddress?.provinceId
    ? getProvinceById(mainApplicant.currentAddress.provinceId)?.abbr
    : "";

  const odometer = aftermarketPurchase?.vehicle?.odometerCount;
  const vin = aftermarketPurchase?.vehicle?.vin;
  const year = aftermarketPurchase?.vehicle?.year;
  const make = aftermarketPurchase?.vehicle?.make;
  const model = aftermarketPurchase?.vehicle?.model;

  const hasValidVehicleInfo = !!vin || (!!year && !!make && !!model);

  const hasProvince = !!provinceAbbr;
  const hasOdometer = !!odometer;

  const handleOnResumeQuoteClick = () => {
    if (!aftermarketPurchase) return;

    if (hasValidVehicleInfo && hasProvince && hasOdometer) {
      const quoteParams: Record<string, string> = {
        source:
          sources
            .find((s) => s.name === aftermarketPurchase.dealSource.toString())
            ?.name.toLowerCase() || "",
        odometer: odometer.toString(),
        province: provinceAbbr,
        removeDiscount: "true",
      };

      if (vin) {
        quoteParams.vin = vin;
      } else {
        quoteParams.year = year?.toString() || "";
        quoteParams.make = make || "";
        quoteParams.model = model || "";
        quoteParams.trim = aftermarketPurchase.vehicle?.trim || "";
      }

      const { href } = aftermarketQuoteStep;
      router.push(`${href}?${new URLSearchParams(quoteParams).toString()}`);
    }
  };

  const showAftermarketResumeQuote =
    !!aftermarketPurchase &&
    showResumeQuoteStep(aftermarketPurchase.currentStepId) &&
    hasValidVehicleInfo &&
    hasProvince &&
    hasOdometer;

  return (
    <nav
      className={classNames("sticky top-0 z-50 py-2", {
        "bg-secondary-950": mode === "dark",
        "bg-white border border-b-2": mode === "light",
      })}
    >
      <div className="mx-6 flex items-center justify-between lg:mx-14 xl:mx-32">
        <div className="flex">
          <Link href="/">
            <LocalImage
              className="w-[90px] h-[40px]"
              width="90"
              height="40"
              src={logoSrc}
              alt="Roadwise Logo"
              priority
            />
          </Link>

          <div className="hidden ml-9 items-center gap-8 font-normal leading-4 lg:flex">
            <Link
              href="/"
              className={classNames("hover:text-opacity-80", {
                "text-white": mode === "dark",
                "text-gray-900": mode === "light",
              })}
            >
              {t("common:home")}
            </Link>

            <NavDropdown
              mode={mode}
              label={t("common:warranty")}
              items={[
                { label: t("purchase:get_a_free_quote"), href: "/warranty" },
                {
                  label: t("purchase:how_it_works"),
                  href: "/warranty/how-it-works",
                },
                { label: t("common:faq"), href: "/warranty/faq" },
              ]}
            />

            <NavDropdown
              mode={mode}
              label={t("common:financing")}
              items={[
                { label: t("purchase:get_pre_approved"), href: "/financing" },
                {
                  label: t("purchase:how_it_works"),
                  href: "/financing/how-it-works",
                },
                { label: t("common:faq"), href: "/financing/faq" },
              ]}
            />

            <Link
              href="/contact"
              className={classNames("hover:text-opacity-80", {
                "text-white": mode === "dark",
                "text-gray-900": mode === "light",
              })}
            >
              {t("common:contact_us")}
            </Link>
          </div>
        </div>

        {/* Desktop Nav */}
        <div className="hidden items-center gap-8 font-normal lg:flex">
          <LocaleButton mode={mode} />
          <hr className="hidden h-12 border-l-[1px] border-gray-400 lg:block" />

          <div className="flex items-center gap-4">
            {showAftermarketResumeQuote && !consumerFinancingPurchase ? (
              <Button
                rightIcon={<ArrowRightIcon />}
                onClick={handleOnResumeQuoteClick}
              >
                {t("purchase:view_my_quote")}
              </Button>
            ) : (
              <Button
                onClick={handleGetStartedModal}
                buttonStyle="dark-secondary"
              >
                {t("common:get_started")}
              </Button>
            )}

            <div className="relative" ref={avatarRef}>
              {user && user.isLoggedIn ? (
                <button
                  type="button"
                  onClick={onAvatarClick}
                  className={classNames("hover:opacity-80", {
                    "text-white": mode === "dark",
                    "text-gray-900": mode === "light",
                  })}
                  aria-haspopup="menu"
                  aria-expanded={isAvatarMenuOpen}
                >
                  <CustomerAvatar customer={user} size="sm" isCurrentUser />
                </button>
              ) : (
                <Link
                  href="#"
                  onClick={onLoginClick}
                  className={classNames("hover:text-opacity-80", {
                    "text-white": mode === "dark",
                    "text-primary-700": mode === "light",
                  })}
                >
                  {t("common:login")}
                </Link>
              )}

              {user && isAvatarMenuOpen && (
                <AvatarMenu
                  onLogoutClick={onLogoutClick}
                  user={user}
                  activePurchase={aftermarketPurchase}
                />
              )}
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="flex items-center space-x-4 lg:hidden">
          <LocaleButton mode={mode} />
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? (
              <CloseIcon
                className={classNames("h-6 w-6", {
                  "text-white": mode === "dark",
                  "text-gray-900": mode === "light",
                })}
              />
            ) : (
              <MenuIcon
                className={classNames("h-6 w-6", {
                  "text-white": mode === "dark",
                  "text-gray-900": mode === "light",
                })}
              />
            )}
          </button>
        </div>
      </div>

      <MobileNavBar
        user={user}
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        onLoginClick={onLoginClick}
        onLogoutClick={onLogoutClick}
        mode={mode}
      />

      {isGetStartedModalOpen && (
        <RoadwiseResumeStartPurchaseModal
          onClose={() => setIsGetStartedModalOpen(false)}
          isOpen={isGetStartedModalOpen}
        />
      )}
    </nav>
  );
};

export default RoadwiseNavBar;
